// ABOUTME: Pure ranking for the header search launcher's dashboard-item search.
// ABOUTME: Kept out of the component so match ordering can be unit tested.

import type { DashboardItem } from "../services/dashboardService";
import { domainOf } from "./linkLabel";

export interface RankedItem {
  item: DashboardItem;
  score: number;
}

/**
 * Scores a single haystack against the query.
 *
 * Higher is better; 0 means no match. The tiers matter more than the exact
 * numbers: an exact hit should always outrank a prefix, a prefix should always
 * outrank a mid-string hit, and a subsequence match is the last resort.
 */
function scoreText(haystack: string, query: string): number {
  if (!haystack) return 0;
  const h = haystack.toLowerCase();

  if (h === query) return 100;
  if (h.startsWith(query)) return 75;

  const idx = h.indexOf(query);
  if (idx > 0) {
    // Matching at a word boundary reads as more intentional than mid-word.
    const boundary = /[\s\-_./]/.test(h[idx - 1]);
    return (boundary ? 55 : 40) - Math.min(idx, 15) * 0.5;
  }

  // Subsequence: "gh" should still find "github".
  let cursor = 0;
  for (const char of query) {
    cursor = h.indexOf(char, cursor);
    if (cursor === -1) return 0;
    cursor += 1;
  }
  return 15;
}

/** Field weights — a name match should beat a URL match on the same query. */
const WEIGHTS = { name: 1, widgetName: 1, domain: 0.9, url: 0.6, tags: 0.7 };

export function scoreItem(item: DashboardItem, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;

  const candidates: Array<[number, string]> = [
    [WEIGHTS.name, item.name || ""],
    [WEIGHTS.widgetName, item.widgetName || ""],
    [WEIGHTS.url, item.url || ""],
    [WEIGHTS.domain, item.url ? domainOf(item.url) : ""],
    ...(item.tags || []).map((tag) => [WEIGHTS.tags, tag] as [number, string]),
  ];

  let best = 0;
  for (const [weight, text] of candidates) {
    best = Math.max(best, scoreText(text, q) * weight);
  }
  return best;
}

/**
 * Ranks dashboard items for a launcher query.
 *
 * Ads are excluded — they're injected for display and shouldn't be presented as
 * one of the user's own links.
 */
export function rankItems(items: DashboardItem[], query: string, limit = 8): DashboardItem[] {
  const q = query.trim();
  if (!q) return [];

  return items
    .filter((item) => !item.isAd)
    .map((item) => ({ item, score: scoreItem(item, q) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      // Tiebreak on label, then id. Without the id fallback, two items with the
      // same name would fall through to input order — which changes every time
      // the dashboard array is reordered by a drag.
      const byLabel = (a.item.name || a.item.url || "").localeCompare(
        b.item.name || b.item.url || ""
      );
      return byLabel !== 0 ? byLabel : a.item.id.localeCompare(b.item.id);
    })
    .slice(0, limit)
    .map((entry) => entry.item);
}
